import classNames from 'classnames'
import React from 'react'
import { InternalLink } from '../../types/InternalLink'
import {
  isNullOrWhiteSpace,
  sanitizeHtmlAndReplaceNewlines,
} from '../../utilities/string'
import { Box } from '../Box'
import { Button } from '../Button'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'
import { LinkList } from '../LinkList'

export interface BannerProps {
  backgroundImageProps?: ImageProps
  buttonTitle?: string
  buttonUrl?: string
  links?: InternalLink[]
  subtitle?: string
  text?: string
  title?: string
}

export const Banner = ({
  backgroundImageProps,
  buttonTitle,
  buttonUrl,
  links: tableRows,
  subtitle,
  text,
  title,
}: BannerProps): JSX.Element => (
  <div
    className={classNames(
      'relative bg-site-background-secondary overflow-hidden py-30',
      'sm:py-90'
    )}
  >
    <Grid
      additionalClassName={classNames(
        'grid-rows-purpose-and-culture h-[61.875rem]',
        'tb:h-480',
        'sm:h-[52.5rem]',
        'xl:h-[70.3125rem]',
        'xxl:h-[93.125rem]'
      )}
      contentAlignment="justify-center"
    >
      {backgroundImageProps && (
        <Image
          {...backgroundImageProps}
          applyLqip
          className={classNames(
            'absolute top-0 left-1/2 transform -translate-x-1/2 mt-330 w-480',
            'tb:mt-0 tb:top-1/2 tb:-translate-y-1/2',
            'sm:w-[53.75rem]',
            'md:w-[68.125rem]',
            'xl:w-[75rem]',
            'xxl:w-[109.6875rem]'
          )}
          caption=""
          copyright=""
          imgClassName="w-full h-full"
          loading="lazy"
        />
      )}

      <div
        className={classNames(
          'relative col-span-full',
          'tb:col-start-1 tb:col-span-2 tb:ml-45',
          'sm:col-start-2 sm:col-span-2 sm:ml-0',
          'md:col-start-1 md:col-span-2',
          'lg:col-start-2 lg:col-span-2',
          'xl:col-start-2 xl:col-span-3',
          'xxl:col-start-3 xxl:col-span-3'
        )}
      >
        {!isNullOrWhiteSpace(title) && (
          <h2
            className={classNames(
              'text-primary mr-120 mb-30 font-h2 text-h2',
              'tb:mr-60 tb:mb-15',
              'sm:mb-30 sm:-mr-75',
              'xl:mr-0'
            )}
          >
            {title}
          </h2>
        )}

        {!isNullOrWhiteSpace(subtitle) && (
          <h3
            className={classNames(
              'text-secondary-dark mb-30 mr-30 font-h3 text-h3',
              'tb:-mr-15',
              'sm:-mr-30',
              'xl:mr-0',
              'xxl:mb-15'
            )}
          >
            {subtitle}
          </h3>
        )}

        {!isNullOrWhiteSpace(text) && (
          <p
            className={classNames(
              'text-secondary-darker mr-30 font-t text-t-400',
              'tb:-mr-15',
              'sm:mb-45 sm:-mr-30 sm:text-t-500',
              'lg:text-t-600',
              'xl:mb-45 xl:mr-0',
              'xxl:mb-45'
            )}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlAndReplaceNewlines(text),
            }}
          />
        )}

        {!isNullOrWhiteSpace(buttonTitle) && !isNullOrWhiteSpace(buttonUrl) && (
          <div
            className={classNames(
              'hidden',
              'sm:col-start-2 sm:col-span-1 sm:inline-block',
              'md:col-start-1',
              'lg:col-start-2',
              'xxl:col-start-3'
            )}
          >
            <Button url={buttonUrl}>{buttonTitle}</Button>
          </div>
        )}
      </div>

      {!!tableRows?.length && (
        <div
          className={classNames(
            'relative col-span-full row-start-5 flex flex-col justify-end mb-60 mx-15',
            'tb:col-start-3 tb:col-span-2 tb:row-span-full tb:mb-30 tb:ml-30 tb:mr-0',
            'sm:col-start-5 sm:col-span-2 sm:mb-0 sm:-ml-30 sm:mr-60',
            'md:ml-30 md:mr-0',
            'lg:col-start-6 lg:col-span-2 lg:mb-90 lg:ml-60 lg:-mr-30',
            'xl:col-start-8 xl:col-span-2 xl:mb-240 xl:mx-0',
            'xxl:col-start-10 xxl:col-span-3 xxl:mb-480 xxl:ml-90 xxl:-mr-30'
          )}
        >
          <Box
            additionalClassName="min-h-240"
            padding="px-45 pt-45 pb-60 xl:pb-75 xxl:pt-60 xxl:px-60"
            paddingStyle="custom"
            variant="border"
          >
            <LinkList
              links={tableRows.map(row => ({
                url: row.url ?? '',
                text: row.title,
                isExternal: false,
              }))}
            />
          </Box>
        </div>
      )}
    </Grid>
  </div>
)
